import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Alert, TableSortLabel, Typography, AlertColor } from "@mui/material";
import {
  typography,
  spacing,
  colors,
} from "../../../../../../../../../assets/styles/tokens";
import { useSelector } from "react-redux";
import { roundStringNumberToInt } from "../../../../../helpers/businessleaderDashboardHelpers";

interface Props {
  weeksTableHeadings: { [key: string]: string };
}

const RecentWeeksTable = ({ weeksTableHeadings }: Props) => {
  const headCells = Object.entries(weeksTableHeadings);
  const { lastTwoWeeksQueryData } = useSelector(
    (state: any) => state.businessLeaderDashBoard,
  );
  const [order, setOrder] = React.useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = React.useState<string>("metric");

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const formatMetricValue = (
    value: number,
    metricName: string,
    fieldName: string,
  ) => {
    if (value === null || value === undefined) return "N/A";
    const metric = metricName.toLowerCase();
    if (fieldName === "trend") {
      return roundStringNumberToInt(value) + "%";
    }
    if (metric === "cvr") {
      return roundStringNumberToInt(value * 100, true) + "%";
    }
    if (metric === "ftd") {
      return roundStringNumberToInt(value);
    }
    return value < 0
      ? `-$${roundStringNumberToInt(Math.abs(value))}`
      : "$" + roundStringNumberToInt(value);
  };

  const weeksTableRows = React.useMemo(() => {
    return (
      lastTwoWeeksQueryData?.query_output?.map((item: any) => ({
        metric: item.Metrics.toUpperCase(),

        rawLast7: item.Last_week,
        rawLast14: item.Last_two_week,
        rawTrend: item.Trend,

        last_7_days: formatMetricValue(
          item.Last_week,
          item.Metrics,
          "last_7_days",
        ),
        days_7_14: formatMetricValue(
          item.Last_two_week,
          item.Metrics,
          "days_7_14",
        ),
        trend: item.Trend,
      })) || []
    );
  }, [lastTwoWeeksQueryData]);

  const sortedRows = React.useMemo(() => {
    const rows = [...weeksTableRows];

    rows.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (orderBy) {
        case "metric":
          aValue = a.metric;
          bValue = b.metric;
          break;
        case "last_7_days":
          aValue = a.rawLast7;
          bValue = b.rawLast7;
          break;
        case "days_7_14":
          aValue = a.rawLast14;
          bValue = b.rawLast14;
          break;
        case "trend":
          aValue = a.rawTrend;
          bValue = b.rawTrend;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });

    return rows;
  }, [weeksTableRows, order, orderBy]);

  const getTrendAlertConfig = (
    trend: number,
    metric: string,
  ): { severity: AlertColor; color: string } => {
    const lowerMetric = metric.toLowerCase();
    const negativeMetrics = ["spend", "cac"];
    const positiveMetrics = ["pv", "profit", "nrpu", "ftd", "cvr"];
    if (negativeMetrics.includes(lowerMetric)) {
      if (trend > 0) {
        return { severity: "error", color: colors.Error[600].$value };
      }
      return { severity: "success", color: colors.Success[600].$value };
    }
    if (positiveMetrics.includes(lowerMetric)) {
      if (trend < 0) {
        return { severity: "error", color: colors.Error[600].$value };
      }
      return { severity: "success", color: colors.Success[600].$value };
    }
    return { severity: "success", color: colors.Success[600].$value };
  };

  return (
    <>
      <Table size="small">
        <TableHead sx={{ background: colors.GrayLightMode[100].$value }}>
          <TableRow>
            {headCells.map(([key, value], index) => (
              <TableCell
                key={index}
                align="left"
                sx={{
                  // whiteSpace: "nowrap",
                  fontSize: typography.Size.Text.xs.$value,
                  fontWeight: typography.Font_weight.Regular.$value,
                  paddingX: spacing.Spacing.xs.$value,
                  paddingY: spacing.Spacing.sm.$value,
                  lineHeight: typography.Line_height.text.xs.$value,
                  color: colors.GrayLightMode[900].$value
                }}
              >
                {key === "metric" ? (
                  value
                ) : (
                  <TableSortLabel
                    active={orderBy === key}
                    direction={orderBy === key ? order : "asc"}
                    onClick={() => handleRequestSort(key)}
                    sx={{
                      "& .MuiTableSortLabel-icon": {
                        display: "none",
                      },
                    }}
                  >
                    {value}
                  </TableSortLabel>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((row: any) => (
            <React.Fragment key={row.metric}>
              <TableRow
                sx={{
                  "& > *": {
                    borderBottom:
                      "1px solid " +
                      colors.GrayLightMode[200].$value +
                      "!important",
                  },
                }}
              >
                {headCells.map(([key], cellIndex) => (
                  <TableCell
                    key={cellIndex}
                    align="left"
                    sx={{
                      whiteSpace: "nowrap",
                      fontSize: typography.Size.Text.xs.$value,
                      fontWeight: typography.Font_weight.Regular.$value,
                      paddingX: spacing.Spacing.xs.$value,
                      paddingY: spacing.Spacing.xs.$value,
                      lineHeight: typography.Line_height.text.xs.$value,
                      color: colors.GrayLightMode[900].$value
                    }}
                  >
                    {key === "metric" ? (
                      <>
                        <Typography
                          sx={{
                            fontSize: typography.Size.Text.xs.$value,
                          }}
                        >
                          {row[key] ? row[key].replace(/_/g, " ") : "N/A"}
                        </Typography>
                      </>
                    ) : key === "last_7_days" ? (
                      <Typography
                        sx={{
                          fontWeight: typography.Font_weight.Semibold.$value,
                          fontSize: typography.Size.Text.xs.$value,
                        }}
                      >
                        {" "}
                        {row[key] ? row[key] : "N/A"}
                      </Typography>
                    ) : key === "days_7_14" ? (
                      <Typography
                        sx={{
                          fontWeight: typography.Font_weight.Semibold.$value,
                          fontSize: typography.Size.Text.xs.$value,
                        }}
                      >
                        {" "}
                        {row[key] ? row[key] : "N/A"}
                      </Typography>
                    ) : key === "trend" ? (
                      <div className="AlertItems">
                        {(() => {
                          const trendValue = row[key];

                          if (typeof trendValue !== "number") return <>N/A</>;

                          const { severity, color } = getTrendAlertConfig(
                            trendValue,
                            row.metric,
                          );
                          return (
                            <Alert
                              severity={severity}
                              icon={false}
                              sx={{
                                "& .MuiAlert-message": {
                                  paddingY:
                                    spacing.Spacing.xxs.$value + " !important",
                                  paddingX:
                                    spacing.Spacing.sm.$value + " !important",
                                },
                                color,
                                fontWeight:
                                  typography.Font_weight.Semibold.$value,
                                paddingX: spacing.Spacing.none.$value,
                                paddingY: spacing.Spacing.none.$value,
                                fontSize: typography.Size.Text.xs.$value,
                                lineHeight:
                                  typography.Line_height.text.xs.$value,
                                display: "inline-flex",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatMetricValue(
                                trendValue,
                                row.metric,
                                "trend",
                              )}
                            </Alert>
                          );
                        })()}
                      </div>
                    ) : (
                      "N/A"
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

export default RecentWeeksTable;
